'use client';

import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	Grid,
	MenuItem,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { gql, useMutation } from '@parkchirp/api-client';
import type { Dayjs } from 'dayjs';
import React, { useCallback, useState } from 'react';

import { GET_PARKER_FOR_PARKER_DETAIL } from '../../../queries';
import { SetShowSnackbar } from '../../../types';
import { dayjs } from '../../../utils';
import { getHistoryErrorMessage, getMinDate, shouldDisableDate } from './shared';

type SublotHistoryModalProps = {
	parkerId: string;
	sublotHistoryId: number;
	sublotHistory: { id: number; startDate: string }[];
	sublots: { id: number; name: string }[];
	parkerHasBeenInvoiced: (date: string) => boolean;
	closeModal: () => Promise<void>;
	setShowSnackbar: SetShowSnackbar;
	tenantSubscriptionId?: string;
	refetchParkersFromCache?: () => void;
	fetchParkerSublotHistory: () => void;
	updateParkerSublotHistory: (args) => void;
	updateInProgress: boolean;
};

const CREATE_PARKER_SUBLOT_HISTORY = gql`
    mutation createParkerSublotHistory(
        $parkerId: String!,
        $sublotId: Int!,
        $startDate: String!,
        $tenantSubscriptionId: String
    ) {
        createParkerSublotHistory(
            parkerId: $parkerId,
            sublotId: $sublotId,
            startDate: $startDate,
            tenantSubscriptionId: $tenantSubscriptionId
        )
    }
`;

const steps = ['Select a date', 'Select a sublot'];

export const EditSublotHistoryModal = ({
	parkerId,
	sublotHistoryId,
	sublotHistory,
	sublots,
	parkerHasBeenInvoiced,
	closeModal,
	setShowSnackbar,
	tenantSubscriptionId,
	refetchParkersFromCache,
	fetchParkerSublotHistory,
	updateParkerSublotHistory,
	updateInProgress,
}: SublotHistoryModalProps) => {
	const editMode = !!sublotHistoryId;
	const editingStartDateRecord = sublotHistoryId === sublotHistory[0].id;
	const billingStartDate = dayjs.utc(sublotHistory[0].startDate);
	const selectedSublotHistory = sublotHistory.find(({ id }) => id === sublotHistoryId);

	const [activeStep, setActiveStep] = useState<number>(0);
	const [startDate, setStartDate] = useState<string>(selectedSublotHistory?.startDate ?? dayjs.utc().format());
	const [sublotId, setSublotId] = useState<number | string>('');
	const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
	const [createParkerSublotHistory, { loading: createInProgress }] = useMutation(CREATE_PARKER_SUBLOT_HISTORY);

	const handleBack = useCallback(() => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
		setSublotId('');
	}, []);

	const handleNext = useCallback(async () => {
		setActiveStep((prevActiveStep) => prevActiveStep + 1);
	}, []);

	const saveSublotHistory = useCallback(async () => {
		try {
			if (editMode) {
				await updateParkerSublotHistory({
					variables: {
						parkerSublotHistoryId: sublotHistoryId,
						sublotId,
						startDate,
						tenantSubscriptionId,
					},
					context: { apiVersion: 'v2' },
					refetchQueries: [
						{
							query: GET_PARKER_FOR_PARKER_DETAIL,
							variables: {
								parkerId,
								tenantSubscriptionId,
							},
						},
						'getParker',
					],
				});
				await fetchParkerSublotHistory();
			} else {
				await createParkerSublotHistory({
					variables: {
						parkerId,
						sublotId,
						startDate,
						tenantSubscriptionId,
					},
					context: { apiVersion: 'v2' },
					refetchQueries: [
						{
							query: GET_PARKER_FOR_PARKER_DETAIL,
							variables: {
								parkerId,
								tenantSubscriptionId,
							},
						},
						'getParker',
					],
				});
				await fetchParkerSublotHistory();
			}
			if (refetchParkersFromCache) {
				await refetchParkersFromCache();
			}
			await closeModal();
		} catch (err) {
			setShowSnackbar({
				message: getHistoryErrorMessage(err, 'Failed to save sublot change'),
				severity: 'error',
				autoHideDuration: 6000,
			});
		}
	}, [
		closeModal,
		createParkerSublotHistory,
		editMode,
		parkerId,
		sublotHistoryId,
		sublotId,
		setShowSnackbar,
		startDate,
		updateParkerSublotHistory,
		tenantSubscriptionId,
		refetchParkersFromCache,
		fetchParkerSublotHistory,
	]);

	const handleSave = useCallback(async () => {
		if (parkerHasBeenInvoiced(startDate)) {
			setShowConfirmationDialog(true);
		} else {
			await saveSublotHistory();
		}
	}, [parkerHasBeenInvoiced, saveSublotHistory, startDate]);

	const handleConfirm = useCallback(async () => {
		setShowConfirmationDialog(false);
		await saveSublotHistory();
	}, [saveSublotHistory]);

	const loading = createInProgress || updateInProgress;

	const minDate = getMinDate(billingStartDate, editingStartDateRecord);

	const nextDisabled =
		dayjs.utc(startDate).isBefore(minDate, 'day') ||
		shouldDisableDate(sublotHistory, sublotHistoryId, dayjs.utc(startDate));

	const confirmationDialog = (
		<Dialog open={showConfirmationDialog}>
			<Grid item textAlign='center' p={3}>
				<Typography>
					An invoice has been created for this parking period so it should be manually adjusted. Are you sure
					you want to continue?
				</Typography>
				<Grid container justifyContent='space-between' pt={3}>
					<Button onClick={() => setShowConfirmationDialog(false)}>Cancel</Button>
					<Button onClick={handleConfirm}>Confirm</Button>
				</Grid>
			</Grid>
		</Dialog>
	);

	return showConfirmationDialog ? (
		confirmationDialog
	) : (
		<Grid container direction='column' rowSpacing={3}>
			<Grid item>
				<Stepper activeStep={activeStep}>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Grid>
			{activeStep === 0 && (
				<Grid item textAlign='center'>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker<Dayjs>
							value={dayjs.utc(startDate)}
							minDate={minDate}
							shouldDisableDate={(date) => shouldDisableDate(sublotHistory, sublotHistoryId, date)}
							disabled={editingStartDateRecord}
							onChange={(value) => setStartDate(value.format())}
							sx={{ width: '60%', outlineStyle: 'none' }}
							slotProps={{
								textField: {
									inputProps: {
										'data-cy': 'sublotHistory-startDate',
										disabled: true,
									},
									variant: 'standard',
								},
							}}
						/>
					</LocalizationProvider>
					<Grid container justifyContent='space-between' pt={3}>
						<Button onClick={closeModal}>Cancel</Button>
						<Button onClick={handleNext} disabled={nextDisabled}>
							Next
						</Button>
					</Grid>
				</Grid>
			)}
			{activeStep === 1 &&
				(loading ? (
					<Grid textAlign='center' pt={4}>
						<CircularProgress />
					</Grid>
				) : (
					<Grid item textAlign='center'>
						<TextField
							select
							value={sublotId}
							onChange={(e) => setSublotId(e.target.value)}
							style={{ width: '100%' }}
						>
							{sublots.map((sublot) => (
								<MenuItem key={sublot.id} value={sublot.id}>
									{sublot.name}
								</MenuItem>
							))}
						</TextField>
						<Grid container justifyContent='space-between' pt={3}>
							<Box>
								<Button onClick={handleBack}>Back</Button>
							</Box>
							<Box>
								<Button onClick={handleSave} disabled={!sublotId}>
									Save
								</Button>
							</Box>
						</Grid>
					</Grid>
				))}
		</Grid>
	);
};
